"use client";

import { Transition } from "@headlessui/react";
import { format } from "date-fns";
import { Info, XCircle } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import * as React from "react";

import parse from "@/components/html-parser";
import { Button } from "@/components/ui/button";
import UILink from "@/components/ui/link";
import { Scroll as ScrollSpy } from "@/components/ui/scroll";
import { Sheet, SheetClose, SheetContent } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import type { Person } from "@/types";

interface AuthorListProps {
	authors: Person[];
	type: "writer" | "editor" | "reviewer" | "fact_checker";
}

interface ResearchersProps {
	reviewers?: Person[];
	writers?: Person[];
	editors?: Person[];
	date?: string;
	updates?: any[];
}

const AuthorList = ({ authors, type }: AuthorListProps) => {
	const [open, setOpen] = React.useState(false);

	const categories = {
		writer: {
			title: "Written by",
			overview: `Examine researchers are trained professionals in critical evidence
				interpretation. They&apos;re responsible for evaluating the full body of
				research (primarily randomized controlled trials), extracting quantitative
				data into the Examine Database, and writing evidence-based answers.
				<br />
				<br />
					<a href="/about/editorial-policy/" class="underline">
					Learn more about our editorial process
					</a>.`,
		},
		editor: {
			title: "Edited by",
			overview: `Examine editors are responsible for clear, accurate, and consistent
				text throughout Examine. They make sure Examine research analysis is
				easy to read and understand.`,
		},
		reviewer: {
			title: "Reviewed by",
			overview: `Examine articles are reviewed before publication and also upon major
              updates, by one or more researchers and healthcare providers. The
              review process is collaborative and rigorous, with
              non-industry-affiliated researchers, physicians, nurses, and other
              professionals engaging in lengthy group discussions to ensure accuracy
              and provide a broader perspective.`,
		},
		fact_checker: {
			title: "Fact-checked by",
			overview: `Examine articles are reviewed before publication and also upon major updates,
				by one or more researchers and healthcare providers. The review process is
				collaborative and rigorous, with non-industry-affiliated researchers,
				physicians, nurses, and other professionals engaging in lengthy group
				discussions to ensure accuracy and provide a broader perspective.`,
		},
	};

	if (!authors?.length) return null;

	return (
		<div className="mt-3.5 flex text-white">
			<div>
				<Tooltip>
					<TooltipTrigger asChild>
						<button className="underline">{categories[type]?.title}</button>
					</TooltipTrigger>
					<TooltipContent>
						<p>{parse(categories[type]?.overview)}</p>
					</TooltipContent>
				</Tooltip>
				:
				<div className="mt-1 ">
					<div className="flex flex-wrap items-center gap-x-1.5">
						{authors?.slice(0, 2).map((author: Person, idx: number) => (
							<Tooltip key={idx} show={!!author.about}>
								<TooltipTrigger className="flex items-center gap-x-1">
									<>
										{idx !== 0 && <div>&#8226;</div>}
										{author.has_expert_page && author.slug ? (
											<Link
												href={`/experts/${author.slug}/`}
												rel="noreferrer"
												className="font-semibold leading-5 hover:underline"
											>
												{author.degree_short
													? `${author.name}, ${author.degree_short}`
													: author.name}
											</Link>
										) : (
											<p className="cursor-default font-semibold leading-5">
												{author.degree_short
													? `${author.name}, ${author.degree_short}`
													: author.name}
											</p>
										)}
									</>
								</TooltipTrigger>
								<TooltipContent variant="overview">
									<div>
										<h6>
											{author.degree_short ? `${author.name}, ${author.degree_short}` : author.name}
										</h6>
										<p>
											<span>
												<Image
													height={90}
													width={90}
													sizes="90px"
													quality={100}
													className="float-left mr-2 mt-1.5 aspect-square rounded-md object-cover"
													src={author.image}
													alt={author.image}
												/>
												{author.about}
											</span>
										</p>
										{author.has_expert_page && author.slug && (
											<UILink href={`/experts/${author.slug}/`}>Read more</UILink>
										)}
									</div>
								</TooltipContent>
							</Tooltip>
						))}
						{authors.length - 2 > 0 && (
							<button
								onClick={() => setOpen(!open)}
								className="rounded border-1 border-info p-0.5 px-1 text-xs font-medium text-info"
							>
								+ {authors.length - 2} {authors.length - 2 === 1 ? "other" : "others"}
							</button>
						)}
					</div>
				</div>
			</div>
			<ResearchersInfo {...{ open, setOpen, authors }} />
		</div>
	);
};

const AuthorImageList = ({ authors }: { authors: Person[] }) => {
	return (
		<div className="flex flex-wrap items-center gap-4">
			{authors?.map((person: Person, idx: number) => (
				<Tooltip key={idx} show={!!person.about}>
					<TooltipTrigger asChild>
						{person.has_expert_page && person.slug ? (
							<Link
								href={`/experts/${person.slug}/`}
								rel="noreferrer"
								className="size-14 shrink-0 overflow-hidden rounded-xl drop-shadow-avatar"
							>
								<Image
									height={56}
									width={56}
									sizes="56px"
									quality={100}
									className="size-full object-cover"
									src={person.image}
									alt={person.name}
								/>
							</Link>
						) : (
							<div className="size-14 shrink-0 overflow-hidden rounded-xl drop-shadow-avatar">
								<Image
									height={56}
									width={56}
									sizes="56px"
									quality={100}
									className="size-full object-cover"
									src={person.image}
									alt={person.name}
								/>
							</div>
						)}
					</TooltipTrigger>
					<TooltipContent variant="overview">
						<div>
							<h6>
								{person.degree_short ? `${person.name}, ${person.degree_short}` : person.name}
							</h6>
							<p>
								<span>
									<Image
										width={90}
										height={90}
										sizes="90px"
										quality={100}
										className="float-left mr-2 mt-1.5 aspect-square rounded-md object-cover"
										src={person.image}
										alt={person.name}
									/>
									{person.about}
								</span>
							</p>
							{person.has_expert_page && person.slug && (
								<UILink href={`/experts/${person.slug}/`}>Read more</UILink>
							)}
						</div>
					</TooltipContent>
				</Tooltip>
			))}
		</div>
	);
};

const ResearchersInfo = ({ open, setOpen, authors }) => {
	const isMobile = useIsMobile();

	const authorList = React.useMemo(
		() => (
			<>
				{authors?.map((author: Person, idx: number) => (
					<div key={idx} className="flex items-center space-x-5">
						{!!author.about ? (
							<div>
								<Tooltip>
									<TooltipTrigger className="flex items-center gap-5">
										{author.has_expert_page && author.slug ? (
											<Link href={`/experts/${author.slug}/`} rel="noreferrer">
												<div className="h-15 w-15 shrink-0 overflow-hidden rounded-xl drop-shadow-avatar">
													<Image
														width={60}
														height={60}
														sizes="60px"
														quality={100}
														className="aspect-square object-cover"
														src={author.image}
														alt={author.name}
													/>
												</div>
											</Link>
										) : (
											<div className="h-15 w-15 shrink-0 overflow-hidden rounded-xl drop-shadow-avatar">
												<Image
													width={60}
													height={60}
													sizes="60px"
													quality={100}
													className="aspect-square object-cover"
													src={author.image}
													alt={author.name}
												/>
											</div>
										)}
										{author.has_expert_page && author.slug ? (
											<Link href={`/experts/${author.slug}/`} rel="noreferrer">
												<p className="inline border-b-2 border-white text-menu font-semibold">
													<span>{author.name}</span>
												</p>
											</Link>
										) : (
											<p className="inline border-b-2 border-white text-menu font-semibold">
												<span>{author.name}</span>
											</p>
										)}
									</TooltipTrigger>
									<TooltipContent variant="overview">
										<div>
											<h6>
												{author.degree_short
													? `${author.name}, ${author.degree_short}`
													: author.name}
											</h6>
											<p>{author.about}</p>
											{author.has_expert_page && author.slug && (
												<UILink href={`/experts/${author.slug}/`}>Read more</UILink>
											)}
										</div>
									</TooltipContent>
								</Tooltip>
							</div>
						) : (
							<>
								<span>
									<div className="h-15 w-15 shrink-0 overflow-hidden rounded-xl drop-shadow-avatar">
										<Image
											width={60}
											height={60}
											sizes="60px"
											quality={100}
											className="aspect-square object-cover"
											src={author.image}
											alt={author.name}
										/>
									</div>
								</span>
								<div>
									<span>
										<p className="inline border-b-2 border-white text-menu font-semibold">
											<span>{author.name}</span>
										</p>
									</span>
								</div>
							</>
						)}
					</div>
				))}
			</>
		),
		[authors]
	);

	if (isMobile) {
		return (
			<Sheet open={open} onOpenChange={setOpen}>
				<SheetContent side="bottom" className="border-none p-0">
					<div className="flex flex-col space-y-6 bg-examine-purple-500 px-10 py-8 text-white">
						{authorList}
					</div>
					<SheetClose asChild>
						<Button className="absolute right-4 top-4" onClick={() => setOpen(!open)}>
							<XCircle size={24} />
						</Button>
					</SheetClose>
				</SheetContent>
			</Sheet>
		);
	}

	return (
		<Transition show={open}>
			<div className="fixed inset-0" onClick={() => setOpen(!open)}></div>
			<Transition.Child
				as="div"
				enter="transform transition ease-in-out duration-300"
				enterFrom="translate-x-full"
				enterTo="translate-x-0"
				leave="transform transition ease-in-out duration-300"
				leaveFrom="translate-x-0"
				leaveTo="translate-x-full"
				className="absolute right-0 top-1/2 z-50 w-full -translate-y-1/2 pl-5"
			>
				<Button className="absolute right-4 top-4" onClick={() => setOpen(!open)}>
					<XCircle size={24} />
				</Button>
				<div className="xxl:px-12 space-y-6 rounded-l-lg bg-examine-purple-500 px-10 pb-6 pt-10 text-white">
					{authorList}
				</div>
			</Transition.Child>
		</Transition>
	);
};

const deduplicateByName = (arr: Person[]) => {
	if (!arr?.length) return [];

	// extract names from array
	const names = arr.map(({ name }) => name);

	// deduplicate array based on name
	const deduplicated = arr.filter(({ name }, idx) => !names.includes(name, idx + 1));

	return deduplicated;
};

const ThreeResearchers = ({ writers = [], reviewers = [], editors = [] }: ResearchersProps) => {
	const authorsUnique = React.useMemo(() => {
		const allAuthors = [...writers, ...editors, ...reviewers];
		const uniqueByName = new Map();
		allAuthors.forEach((author) => {
			if (!uniqueByName.has(author.name)) {
				uniqueByName.set(author.name, author);
			}
		});

		return Array.from(uniqueByName.values());
	}, [writers, editors, reviewers]);

	const oneAuthor =
		authorsUnique.length === 1 &&
		(writers.length === 1 || editors.length === 1 || reviewers.length === 1);

	return (
		<div className={cn(oneAuthor && "flex space-x-4")}>
			<AuthorImageList authors={authorsUnique.slice(0, 4)} />
			<AuthorList authors={writers} type="writer" />
			<AuthorList authors={editors} type="editor" />
			<AuthorList authors={reviewers} type="reviewer" />
		</div>
	);
};

const MobileResearchers = ({ writers = [], reviewers = [], date }: ResearchersProps) => {
	const [open, setOpen] = React.useState(false);

	const authors = React.useMemo(() => [...writers, ...reviewers], [writers, reviewers]);

	return (
		<div className="block lg:hidden">
			<div className="mt-2 flex flex-wrap gap-1 md:flex-col">
				<div className="flex flex-wrap items-center gap-1 text-white">
					{writers?.length > 0 && (
						<div className="flex space-x-1">
							<span className="flex space-x-1 text-13">
								<Tooltip>
									<TooltipTrigger asChild>
										<span className="text-left leading-5 underline">Written </span>
									</TooltipTrigger>
									<TooltipContent>
										<p>
											Examine researchers are trained professionals in critical evidence
											interpretation. They&apos;re responsible for evaluating the full body of
											research (primarily randomized controlled trials), extracting quantitative
											data into the Examine Database, and writing evidence-based answers.
										</p>
									</TooltipContent>
								</Tooltip>
								<span className="leading-5">by:</span>
							</span>
							<div className="flex space-x-1 text-13">
								<span className="text-left leading-5">
									<a href="/about/#researchers" target="_blank">
										{writers[0].name}
										{writers[1] && <span>, {writers[1].name}</span>}
									</a>
								</span>{" "}
								{writers.length - 2 > 0 && (
									<Button
										size="sm"
										onClick={() => setOpen(!open)}
										className="rounded border-1 border-info p-0.5 px-1 text-xs font-medium text-info"
									>
										<span className="sm:hidden">+ {writers.length - 2}</span>{" "}
										<span className="hidden sm:inline">
											+ {writers.length - 2} {writers.length - 2 === 1 ? "other" : "others"}
										</span>
									</Button>
								)}
							</div>
						</div>
					)}{" "}
				</div>
				<div className="flex flex-wrap gap-1">
					{reviewers?.length > 0 && (
						<div className="text-white">
							<div className="flex space-x-1">
								<span className=" flex gap-1 text-13 leading-5">
									<Tooltip>
										<TooltipTrigger asChild>
											<p className="whitespace-nowrap underline">Fact-checked </p>
										</TooltipTrigger>
										<TooltipContent>
											<div className="general-tooltip">
												<p>
													Examine articles are reviewed before publication and also upon major
													updates, by one or more researchers and healthcare providers. The review
													process is collaborative and rigorous, with non-industry-affiliated
													researchers, physicians, nurses, and other professionals engaging in
													lengthy group discussions to ensure accuracy and provide a broader
													perspective.
												</p>
											</div>
										</TooltipContent>
									</Tooltip>
									<p>by:</p>
								</span>

								<div className="text-13">
									<span className="text-left leading-5 ">
										<a href="/about/#reviewers" target="_blank">
											{reviewers[0].name}
											{reviewers[1] && <span>, {reviewers[1].name}</span>}
										</a>
									</span>{" "}
									{reviewers.length - 2 > 0 && (
										<Button
											size="sm"
											onClick={() => setOpen(!open)}
											className="inline rounded border-1 border-info p-0.5 px-1 text-xs font-medium text-info"
										>
											<span className="sm:hidden">+ {reviewers.length - 2}</span>{" "}
											<span className="hidden sm:inline">
												+ {reviewers.length - 2} {reviewers.length - 2 === 1 ? "other" : "others"}{" "}
											</span>
										</Button>
									)}
								</div>
							</div>
						</div>
					)}
					<div className="flex text-13 leading-5 text-white">
						<span className="hidden last:hidden md:mx-2 md:block">•</span>
						<span>Last Updated: {date && format(new Date(date), "MMMM d',' yyyy")}</span>
					</div>
				</div>
			</div>

			<ResearchersInfo {...{ open, setOpen, authors }} />
		</div>
	);
};

const GuidesResearchers = ({
	writers = [],
	editors = [],
	reviewers = [],
	date,
	updates,
}: ResearchersProps) => {
	const authorsUnique = React.useMemo(() => {
		const allAuthors = [...writers, ...editors, ...reviewers];
		const uniqueByName = new Map();
		allAuthors.forEach((author) => {
			if (!uniqueByName.has(author.name)) {
				uniqueByName.set(author.name, author);
			}
		});

		return Array.from(uniqueByName.values());
	}, [writers, editors, reviewers]);

	return (
		<div className="space-y-3.5">
			<AuthorImageList authors={authorsUnique.slice(0, 4)} />
			<AuthorList authors={writers} type="writer" />
			<AuthorList authors={editors} type="editor" />
			<AuthorList authors={reviewers} type="reviewer" />
			<LastUpdated date={date} updates={updates} />
		</div>
	);
};

const LastUpdated = ({ date, updates }: Pick<ResearchersProps, "date" | "updates">) => {
	return (
		<>
			{date ? (
				<p className="mt-2 flex items-center space-x-5 text-sm text-white lg:mt-3.5">
					Last Updated:
					<Tooltip>
						<TooltipTrigger asChild>
							<span className="ml-1 flex cursor-pointer items-center gap-1">
								{date && format(new Date(date), "MMMM d',' yyyy")}
								<Info size={16} />
							</span>
						</TooltipTrigger>
						<TooltipContent>
							<p>
								Our{" "}
								<Link href={"/about/#researchers"} className="underline">
									team of researchers
								</Link>{" "}
								are constantly monitoring and{" "}
								<Link href="/summaries/" className="underline">
									summarizing the latest research
								</Link>
								, and we continue to update our pages to ensure you have the most accurate
								information.
								{updates[0]?.notes ? (
									<>
										<br />
										<br />
										Note on the last update: {updates[0]?.notes.replace(/<\/?[^>]+(>|$)/g, "")}
									</>
								) : null}
								{updates?.length ? (
									<>
										<br />
										<br />
										<ScrollSpy
											to={"updates"}
											spy={true}
											offset={-120}
											smooth={true}
											hashSpy={true}
											duration={75}
											activeClass="isActive-link"
											className="cursor-pointer underline"
										>
											See full history
										</ScrollSpy>
									</>
								) : null}
							</p>
						</TooltipContent>
					</Tooltip>
				</p>
			) : null}
		</>
	);
};

const FourResearchers = ({
	writers = [],
	reviewers = [],
	date,
	updates = [],
}: ResearchersProps) => {
	const deduplicatedAuthors = React.useMemo(
		() => deduplicateByName([...writers.slice(0, 2), ...reviewers.slice(0, 2)]),
		[writers, reviewers]
	);

	const oneAuthor = React.useMemo(() => {
		const authors = [...writers, ...reviewers];

		return (writers?.length === 1 || reviewers?.length === 1) && authors?.length === 1;
	}, [writers, reviewers]);

	return (
		<div>
			<div className={cn(oneAuthor ? "flex items-center space-x-4" : "")}>
				<AuthorImageList authors={deduplicatedAuthors} />
				<AuthorList authors={writers} type="writer" />
				<AuthorList authors={reviewers} type="fact_checker" />
			</div>
			<LastUpdated {...{ date, updates }} />
		</div>
	);
};

export default FourResearchers;

export { FourResearchers, MobileResearchers, ResearchersInfo, ThreeResearchers, GuidesResearchers };
